import moment from "moment";
import {
    TOKYO_STATION_PLACE_ID,
    HANEDA_AIRPORT_PLACE_ID,
    NARITA_AIRPORT_PLACE_ID,
    SHINJUKU_STATION_PLACE_ID,
    SHINAGAWA_STATION_PLACE_ID,
    TRAVEL_AREA_OPTIONS,
    PASSENGER_TITLE_OPTIONS,
    PASSENGER_TITLE_WITH_EMPTY_OPTIONS,
} from "./constants";

// ─── Coordinate helpers ────────────────────────────────────────────

export const getCoordinate = (locationData, key) => {
    if (!locationData) return null;
    if (typeof locationData?.[key] === "function") {
        return locationData[key]();
    }
    return locationData?.[key] ?? null;
};

export const toNumericCoordinate = (value) => {
    if (typeof value === "number") return value;
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
};

export const normalizeLocation = (locationData) => {
    if (!locationData) return null;

    const latRaw =
        typeof locationData?.lat === "function"
            ? locationData.lat()
            : locationData?.lat ?? locationData?.latitude;
    const lonRaw =
        typeof locationData?.lon === "function"
            ? locationData.lon()
            : locationData?.lon ?? locationData?.lng ?? locationData?.longitude;

    const lat = toNumericCoordinate(latRaw);
    const lon = toNumericCoordinate(lonRaw);

    if (lat === null || lon === null) return null;

    return { ...locationData, lat, lon };
};

// ─── Night-hour / surcharge helpers ────────────────────────────────

// Meet & Assist price maths lives in utils/meetAssist.js next to the location
// rules that decide which of these prices a leg is charged.
export {
    isNightHours,
    calculateNightSurcharge,
    calculateVipMeetAssistPrice,
    createMeetAssistPriceStructure,
} from "../../../utils/meetAssist";

// ─── Duration helpers ──────────────────────────────────────────────

export const generateDurationOptions = (lang, minimalDuration = 0) => {
    const startFrom = Math.round(minimalDuration / 30);
    const halfHourIntervals = Array.from({ length: 49 }, (_, i) => {
        const hours = Math.floor(i / 2);
        const minutes = (i % 2) * 30;
        const time = `${hours.toString().padStart(2, "0")}:${minutes
            .toString()
            .padStart(2, "0")}`;
        const label =
            lang === "EN" || lang === "en"
                ? `${hours.toString().padStart(2, "0")} Hours ${minutes
                    .toString()
                    .padStart(2, "0")} Minutes`
                : `${hours.toString().padStart(2, "0")}時間 ${minutes
                    .toString()
                    .padStart(2, "0")}分`;
        return { value: time, label };
    });
    return halfHourIntervals.slice(startFrom + 2);
};

export const getTotalTime = (carRoutes = []) => {
    const summary = carRoutes?.[carRoutes.length - 1]?.summary;
    return summary?.move?.time;
};

// ─── Location check helpers ────────────────────────────────────────

// Both of these are the shared rules from utils/meetAssist.js; re-exported so
// this folder's existing imports keep working.
export {
    checkLocationUseMeetService,
    checkLocationUseTerminalSelection,
} from "../../../utils/meetAssist";

export const checkIsPickupTokyoStation = (pickupLocationData, transferFlags) => {
    const pickupPlaceId = pickupLocationData?.place_id || pickupLocationData?.value;
    return (
        Boolean(transferFlags?.isStationTransfer) &&
        Boolean(transferFlags?.isFromStation) &&
        pickupPlaceId === TOKYO_STATION_PLACE_ID
    );
};

export const checkIsPickupHaneda = (pickupLocationData, transferFlags) => {
    const pickupPlaceId = pickupLocationData?.place_id || pickupLocationData?.value;
    return (
        Boolean(transferFlags?.isAirportTransfer) &&
        Boolean(transferFlags?.isFromAirport) &&
        pickupPlaceId === HANEDA_AIRPORT_PLACE_ID
    );
};

export const checkIsDropoffHaneda = (lastDestinationData, transferFlags) => {
    const lastDestinationPlaceId = lastDestinationData?.place_id || lastDestinationData?.value;
    return (
        Boolean(transferFlags?.isAirportTransfer) &&
        Boolean(transferFlags?.isToAirport) &&
        lastDestinationPlaceId === HANEDA_AIRPORT_PLACE_ID
    );
};

// ─── Format helpers ────────────────────────────────────────────────

export const formatTravelArea = (selectedTravelArea) => {
    if (!selectedTravelArea) return null;
    return TRAVEL_AREA_OPTIONS.find((option) => option.value === selectedTravelArea) || null;
};

export const formatPassengerTitle = (selectedTitle) => {
    if (!selectedTitle) return null;
    return PASSENGER_TITLE_OPTIONS.find((option) => option.value === selectedTitle) || null;
};

export const formatPickupSignHonorific = (selectedTitle) => {
    if (!selectedTitle) return null;
    return PASSENGER_TITLE_WITH_EMPTY_OPTIONS.find((option) => option.value === selectedTitle) || null;
};

// ─── Place selection helpers ───────────────────────────────────────

export const enrichPlaceData = (place) => {
    if (!place) return null;

    const level1 = place.address_components?.find((component) =>
        component.types.includes("administrative_area_level_1")
    );
    const city = level1?.long_name || null;

    place.lat = place.geometry?.location?.lat;
    place.lon = place.geometry?.location?.lng;
    place.label = place.formatted_address;
    place.value = place.place_id;
    place.city = city;
    place.prefecture = city;

    return place;
};

// ─── Booking detail parsing helpers ────────────────────────────────

export const normalizeTerminalValue = (value) => {
    if (!value) return "";
    const normalized = String(value).trim().toLowerCase();
    if (
        normalized === "terminal-1-domestic" ||
        normalized === "terminal-2-domestic" ||
        normalized === "terminal-2-international" ||
        normalized === "terminal-3-international"
    ) {
        return normalized;
    }

    const terminalWithDashMatch = normalized.match(/^terminal-(\d)$/);
    if (terminalWithDashMatch) return `terminal-${terminalWithDashMatch[1]}`;

    const terminalWithSpaceMatch = normalized.match(/^terminal\s*(\d)$/);
    if (terminalWithSpaceMatch) return `terminal-${terminalWithSpaceMatch[1]}`;

    if (/^\d$/.test(normalized)) return `terminal-${normalized}`;

    return normalized;
};

export const getLocationFromFormattedAddress = (formattedAddress = "") => {
    if (!formattedAddress) return "";
    return formattedAddress;
};

export const mapPaxToState = (bookingInfo = []) => {
    const result = { adult: 0, child: 0, toddler: 0, baby: 0 };

    bookingInfo.forEach((booking) => {
        (booking?.pax || []).forEach((paxItem) => {
            const key = (paxItem?.paxtype || "").toLowerCase();
            const count = Number(paxItem?.paxcount || 0);
            if (key in result) {
                result[key] += count;
            }
        });
    });

    return result;
};

export const mapPaxToStateNew = (booking) => {
    const result = { adult: 0, child: 0, toddler: 0, baby: 0 };
    (booking?.pax || []).forEach((paxItem) => {
        const key = (paxItem?.paxtype || "").toLowerCase();
        const count = Number(paxItem?.paxcount || 0);
        if (key in result) {
            result[key] += count;
        }
    });
    return result;
}

export const mapLuggageToState = (bookingInfo = []) => {
    const result = { cabin: 0, checkin: 0 };

    bookingInfo.forEach((booking) => {
        (booking?.luggage || []).forEach((luggageItem) => {
            const type = (luggageItem?.luggagetype || "").toLowerCase();
            const count = Number(luggageItem?.luggagecount || 0);

            if (type.includes("check")) {
                result.checkin += count;
            } else if (type.includes("cabin")) {
                result.cabin += count;
            }
        });
    });

    return result;
};

export const mapLuggageToStateNew = (booking) => {
    const result = { cabin: 0, checkin: 0 };
    (booking?.luggage || []).forEach((luggageItem) => {
        const type = (luggageItem?.luggagetype || "").toLowerCase();
        const count = Number(luggageItem?.luggagecount || 0);

        if (type.includes("check")) {
            result.checkin += count;
        } else if (type.includes("cabin")) {
            result.cabin += count;
        }
    });
    return result;
}

export const mapVehiclesToState = (bookingInfo = []) => {
    if (!bookingInfo.length) return [{ id: 1, selectedVehicle: "" }];
    return bookingInfo.map((booking, index) => ({
        id: index + 1,
        selectedVehicle: booking?.vehicleTypeName || "",
    }));
};

export const extractMessage = (messages = []) => {
    const customerMessage = messages.find((msg) => !msg?.isAdmin && msg?.note);
    return customerMessage?.note || "";
};

export const parseTitleAndName = (fullValue = "") => {
    const titleCandidates = ["Mr.", "Mrs.", "Ms."];
    const matchedTitle = titleCandidates.find((title) => fullValue.startsWith(`${title} `));

    if (!matchedTitle) {
        return { title: "Mr.", name: fullValue || "" };
    }

    return {
        title: matchedTitle,
        name: fullValue.replace(`${matchedTitle} `, ""),
    };
};

// ─── Transfer flag helpers ─────────────────────────────────────────

export const computeTransferFlags = (pickupLocationData, lastDestinationData) => {
    const pickupPlaceId = pickupLocationData?.place_id || pickupLocationData?.value;
    const dropoffPlaceId = lastDestinationData?.place_id || lastDestinationData?.value;

    const nextFlags = {
        isFromAirport: pickupPlaceId === HANEDA_AIRPORT_PLACE_ID || pickupPlaceId === NARITA_AIRPORT_PLACE_ID,
        isToAirport: dropoffPlaceId === HANEDA_AIRPORT_PLACE_ID || dropoffPlaceId === NARITA_AIRPORT_PLACE_ID,
        isFromStation:
            pickupPlaceId === TOKYO_STATION_PLACE_ID ||
            pickupPlaceId === SHINJUKU_STATION_PLACE_ID ||
            pickupPlaceId === SHINAGAWA_STATION_PLACE_ID,
        isToStation:
            dropoffPlaceId === TOKYO_STATION_PLACE_ID ||
            dropoffPlaceId === SHINJUKU_STATION_PLACE_ID ||
            dropoffPlaceId === SHINAGAWA_STATION_PLACE_ID,
    };

    nextFlags.isAirportTransfer = nextFlags.isFromAirport || nextFlags.isToAirport;
    nextFlags.isStationTransfer = nextFlags.isFromStation || nextFlags.isToStation;

    return nextFlags;
};

// ─── Total pax helper ──────────────────────────────────────────────

export const getTotalPax = (passengers) => {
    return (
        Number(passengers?.adult || 0) +
        Number(passengers?.child || 0) +
        Number(passengers?.toddler || 0) +
        Number(passengers?.baby || 0)
    );
};

export const getLocationDetail = (loc) => {
    const formattedAddressArr = loc?.formatted_address?.split(",");
    const name = formattedAddressArr?.[0];
    const address = formattedAddressArr?.slice(1).join(",");

    return {
        name,
        address
    }
}